import React from 'react';
import { cn } from '@/utils/classNames';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'accent' | 'ghost' | 'glass' | 'outline';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  isLoading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  isGlowing?: boolean;
  hasRipple?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  className,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  icon,
  iconPosition = 'left',
  isGlowing = false,
  hasRipple = false,
  disabled,
  ...props
}) => {
  // Create ripple effect on click if enabled
  const handleRipple = hasRipple
    ? (e: React.MouseEvent<HTMLButtonElement>) => {
        const button = e.currentTarget;
        const rect = button.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const ripple = document.createElement('span');
        ripple.style.left = `${x}px`;
        ripple.style.top = `${y}px`;
        ripple.className = 'absolute rounded-full bg-white bg-opacity-30 animate-ripple';

        button.appendChild(ripple);

        setTimeout(() => {
          ripple.remove();
        }, 600);
      }
    : undefined;

  // Base styles for all buttons
  const baseStyles =
    'relative inline-flex items-center justify-center rounded-md font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none overflow-hidden';

  // Variant-specific styles
  const variantStyles = {
    primary:
      'bg-primary text-white hover:bg-primary-hover active:bg-primary-active focus-visible:ring-primary/50',
    secondary:
      'bg-secondary text-white hover:bg-secondary-hover active:bg-secondary-active focus-visible:ring-secondary/50',
    accent:
      'bg-accent text-white hover:bg-accent-hover active:bg-accent-active focus-visible:ring-accent/50',
    ghost:
      'bg-transparent hover:bg-hover active:bg-hover/50 text-text-primary dark:text-text-primary',
    glass:
      'glass dark:glass-dark backdrop-blur text-text-primary dark:text-text-primary hover:bg-white/10 dark:hover:bg-black/10',
    outline:
      'bg-transparent border border-primary text-primary hover:bg-primary hover:text-white active:bg-primary-active',
  };

  // Size-specific styles
  const sizeStyles = {
    xs: 'text-xs px-2 py-1 h-6 rounded',
    sm: 'text-xs px-2.5 py-1.5 h-8',
    md: 'text-sm px-4 py-2 h-10',
    lg: 'text-base px-5 py-2.5 h-12',
    xl: 'text-lg px-6 py-3 h-14',
  };

  // Glowing effect classes
  const glowClasses = isGlowing
    ? variant === 'accent'
      ? 'border-glow-accent'
      : 'border-glow'
    : '';

  return (
    <button
      className={cn(baseStyles, variantStyles[variant], sizeStyles[size], glowClasses, className)}
      disabled={disabled || isLoading}
      onClick={handleRipple}
      {...props}
    >
      {isLoading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4 text-current"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      )}

      {icon && !isLoading && iconPosition === 'left' && (
        <span className="mr-2 flex-shrink-0">{icon}</span>
      )}

      <span>{children}</span>

      {icon && !isLoading && iconPosition === 'right' && (
        <span className="ml-2 flex-shrink-0">{icon}</span>
      )}

      {/* Ripple animation container */}
      {hasRipple && <span className="ripple-container"></span>}
    </button>
  );
};
